"use strict";
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const turf_1 = require("@turf/turf");
const uniq_1 = __importDefault(require("lodash/uniq"));
const ServiceLocator_1 = __importDefault(require("chaire-lib-common/lib/utils/ServiceLocator"));
const defaultGeojson = (0, turf_1.featureCollection)([]);
/**
 * Layer manager for MapLibre GL maps
 *
 * TODO See how filters are used and type them properly, make them map implementation independant ideally
 *
 * TODO: If we want to support multiple map implementation, this layer management will have to be updated
 */
class MapLibreLayerManager {
    constructor(layersConfig, map = undefined) {
        this._layersByName = {};
        this._enabledLayers = [];
        this._defaultFilterByLayer = {};
        this._filtersByLayer = {};
        this._map = map;
        for (const layerName in layersConfig) {
            const source = {
                type: 'geojson',
                data: defaultGeojson
            };
            const inputConfig = layersConfig[layerName];
            const { defaultFilter, customLayer } = inputConfig, layerProps = __rest(inputConfig, ["defaultFilter", "customLayer"]);
            // Build the layer specification with required id and source
            const layer = Object.assign(Object.assign(Object.assign({}, layerProps), { id: layerName, source: layerName }), (defaultFilter ? { filter: defaultFilter } : {}));
            if (defaultFilter) {
                this._defaultFilterByLayer[layerName] = defaultFilter;
                this._filtersByLayer[layerName] = defaultFilter;
            }
            else {
                this._filtersByLayer[layerName] = undefined;
                this._defaultFilterByLayer[layerName] = undefined;
            }
            this._layersByName[layerName] = {
                layer,
                source,
                customLayer: customLayer === null || customLayer === void 0 ? void 0 : customLayer()
            };
        }
    }
    // TODO Consider deprecating and adding the map on the constructor only
    setMap(map) {
        this._map = map;
    }
    showLayer(layerName) {
        var _a;
        (_a = this._map) === null || _a === void 0 ? void 0 : _a.setLayoutProperty(layerName, 'visibility', 'visible');
    }
    hideLayer(layerName) {
        var _a;
        (_a = this._map) === null || _a === void 0 ? void 0 : _a.setLayoutProperty(layerName, 'visibility', 'none');
    }
    getFilter(layerName) {
        return this._filtersByLayer[layerName] || null;
    }
    updateFilter(layerName, filter) {
        var _a, _b;
        const defaultFilter = this._defaultFilterByLayer[layerName];
        if (defaultFilter && filter) {
            // Combine with default filter using 'all' expression
            filter = ['all', defaultFilter, filter];
        }
        else if (defaultFilter) {
            filter = defaultFilter;
        }
        this._filtersByLayer[layerName] = filter;
        if (this.layerIsEnabled(layerName)) {
            // Use nullish coalescing to pass null instead of undefined to MapLibre
            (_a = this._map) === null || _a === void 0 ? void 0 : _a.setFilter(layerName, ((_b = this._filtersByLayer[layerName]) !== null && _b !== void 0 ? _b : null));
        }
    }
    clearFilter(layerName) {
        var _a, _b;
        this._filtersByLayer[layerName] = this._defaultFilterByLayer[layerName];
        if (this.layerIsEnabled(layerName)) {
            (_a = this._map) === null || _a === void 0 ? void 0 : _a.setFilter(layerName, ((_b = this._defaultFilterByLayer[layerName]) !== null && _b !== void 0 ? _b : null));
        }
    }
    updateEnabledLayers(enabledLayers = []) {
        if (!this._map) {
            return;
        }
        enabledLayers = (0, uniq_1.default)(enabledLayers); // make sure we do not have the same layer twice (can happen with user prefs not replaced correctly after updates)
        const previousEnabledLayers = this._enabledLayers || [];
        previousEnabledLayers.forEach((previousEnabledLayer) => {
            var _a, _b, _c, _d, _e, _f, _g, _h;
            // Try to remove layer if it exists (it might have been removed by style change)
            try {
                const customLayerId = (_b = (_a = this._layersByName[previousEnabledLayer]) === null || _a === void 0 ? void 0 : _a.customLayer) === null || _b === void 0 ? void 0 : _b.id;
                if (customLayerId && ((_c = this._map) === null || _c === void 0 ? void 0 : _c.getLayer(customLayerId))) {
                    (_d = this._map) === null || _d === void 0 ? void 0 : _d.removeLayer(customLayerId);
                }
                if ((_e = this._map) === null || _e === void 0 ? void 0 : _e.getLayer(previousEnabledLayer)) {
                    (_f = this._map) === null || _f === void 0 ? void 0 : _f.removeLayer(previousEnabledLayer);
                }
            }
            catch (e) {
                console.error('Layer not found, ignoring it', e);
                // Layer doesn't exist, which is fine
            }
            if (!enabledLayers.includes(previousEnabledLayer)) {
                // Try to remove source if it exists
                try {
                    if ((_g = this._map) === null || _g === void 0 ? void 0 : _g.getSource(previousEnabledLayer)) {
                        (_h = this._map) === null || _h === void 0 ? void 0 : _h.removeSource(previousEnabledLayer);
                    }
                }
                catch (e) {
                    console.error('Source not found, ignoring it', e);
                    // Source doesn't exist, which is fine
                }
            }
        });
        const enabledAndActiveLayers = [];
        enabledLayers.forEach((enabledLayer) => {
            var _a, _b, _c, _d, _e, _f, _g, _h;
            if (!this._layersByName[enabledLayer]) {
                // Layer not defined
                return;
            }
            // Check if source exists on the map (it might have been removed by style change)
            // If it doesn't exist, add it
            if (!((_a = this._map) === null || _a === void 0 ? void 0 : _a.getSource(enabledLayer))) {
                (_b = this._map) === null || _b === void 0 ? void 0 : _b.addSource(enabledLayer, this._layersByName[enabledLayer].source);
            }
            // Check if layer exists before adding
            if (!((_c = this._map) === null || _c === void 0 ? void 0 : _c.getLayer(enabledLayer))) {
                (_d = this._map) === null || _d === void 0 ? void 0 : _d.addLayer(this._layersByName[enabledLayer].layer);
            }
            const customLayer = this._layersByName[enabledLayer].customLayer;
            if (customLayer && !((_e = this._map) === null || _e === void 0 ? void 0 : _e.getLayer(customLayer.id))) {
                (_f = this._map) === null || _f === void 0 ? void 0 : _f.addLayer(customLayer);
            }
            (_g = this._map) === null || _g === void 0 ? void 0 : _g.setFilter(enabledLayer, ((_h = this._filtersByLayer[enabledLayer]) !== null && _h !== void 0 ? _h : null));
            enabledAndActiveLayers.push(enabledLayer);
        });
        this._enabledLayers = enabledAndActiveLayers;
        ServiceLocator_1.default.eventManager.emit('map.updatedEnabledLayers', enabledLayers);
    }
    showLayerObjectByAttribute(layerName, attribute, value) {
        var _a;
        const existingFilter = this.getFilter(layerName);
        let values;
        let newFilter;
        if (existingFilter &&
            existingFilter[0] === 'match' &&
            existingFilter[1] &&
            existingFilter[1][0] === 'get' &&
            existingFilter[1][1] === attribute) {
            const filterValues = existingFilter[2];
            if (filterValues && Array.isArray(filterValues)) {
                if (filterValues.includes(value)) {
                    // Value already exists, preserve existing filter values (no change needed)
                    return;
                }
                else {
                    // Clone existing values and add the new value
                    values = [...filterValues];
                    values.push(value);
                }
            }
            else {
                values = [value];
            }
            existingFilter[2] = values;
            newFilter = existingFilter;
        }
        else {
            // Initialize with [value] so the filter matches the specified value
            newFilter = ['match', ['get', attribute], [value], true, false];
        }
        // Update both map and internal cache
        (_a = this._map) === null || _a === void 0 ? void 0 : _a.setFilter(layerName, newFilter);
        this._filtersByLayer[layerName] = newFilter;
    }
    hideLayerObjectByAttribute(layerName, attribute, value) {
        var _a, _b;
        const existingFilter = this.getFilter(layerName);
        if (existingFilter &&
            existingFilter[0] === 'match' &&
            existingFilter[1] &&
            existingFilter[1][0] === 'get' &&
            existingFilter[1][1] === attribute) {
            if (existingFilter[2] && Array.isArray(existingFilter[2])) {
                const values = existingFilter[2];
                const valueIndex = values.indexOf(value);
                if (valueIndex >= 0) {
                    // Remove the value from the filter
                    values.splice(valueIndex, 1);
                    if (values.length === 0) {
                        // No more values to match, clear the filter
                        (_a = this._map) === null || _a === void 0 ? void 0 : _a.setFilter(layerName, null);
                        this._filtersByLayer[layerName] = null;
                    }
                    else {
                        existingFilter[2] = values;
                        const newFilter = existingFilter;
                        (_b = this._map) === null || _b === void 0 ? void 0 : _b.setFilter(layerName, newFilter);
                        this._filtersByLayer[layerName] = newFilter;
                    }
                }
            }
        }
        // If no existing match filter for this attribute, nothing to hide
    }
    getLayer(layerName) {
        var _a;
        return (_a = this._map) === null || _a === void 0 ? void 0 : _a.getLayer(layerName);
    }
    getNextLayerName(layerName) {
        // to be able to add a layer before another (see maplibre map.addLayer attribute beforeId)
        const enabledLayers = this._enabledLayers || [];
        const enabledLayersCount = enabledLayers.length;
        for (let i = 0; i < enabledLayersCount - 1; i++) {
            //  return undefined is already last (i < length - 1)
            const enabledLayerName = this._enabledLayers[i];
            if (enabledLayerName === layerName) {
                return this._enabledLayers[i + 1];
            }
        }
        return undefined;
    }
    updateLayer(layerName, geojson) {
        var _a;
        const newGeojson = typeof geojson === 'function'
            ? geojson(this._layersByName[layerName].source.data)
            : geojson
                ? geojson
                : defaultGeojson;
        this._layersByName[layerName].source.data = newGeojson;
        (_a = this._layersByName[layerName].customLayer) === null || _a === void 0 ? void 0 : _a.setData(newGeojson.features);
        if (this._map && this.layerIsEnabled(layerName)) {
            const source = this._map.getSource(layerName);
            source === null || source === void 0 ? void 0 : source.setData(this._layersByName[layerName].source.data);
        }
        ServiceLocator_1.default.eventManager.emit('map.updatedLayer', layerName);
    }
    updateLayers(geojsonByLayerName) {
        var _a;
        for (const layerName in geojsonByLayerName) {
            const geojson = geojsonByLayerName[layerName];
            const newGeojson = typeof geojson === 'function'
                ? geojson(this._layersByName[layerName].source.data)
                : geojson
                    ? geojson
                    : defaultGeojson;
            this._layersByName[layerName].source.data = newGeojson;
            (_a = this._layersByName[layerName].customLayer) === null || _a === void 0 ? void 0 : _a.setData(newGeojson.features);
            if (this._map && this.layerIsEnabled(layerName)) {
                const source = this._map.getSource(layerName);
                source === null || source === void 0 ? void 0 : source.setData(this._layersByName[layerName].source.data);
            }
        }
        ServiceLocator_1.default.eventManager.emit('map.updatedLayers', Object.keys(geojsonByLayerName));
    }
    /**
     * Start or stop the animation of all custom layers.
     * @param enabled Whether the custom layers are animated
     */
    setAnimationEnabled(enabled) {
        Object.values(this._layersByName).forEach((entry) => { var _a; return (_a = entry.customLayer) === null || _a === void 0 ? void 0 : _a.setAnimationEnabled(enabled); });
    }
    layerIsEnabled(layerName) {
        return this.getEnabledLayers().includes(layerName);
    }
    layerIsVisible(layerName) {
        if (!this._map) {
            return null;
        }
        const layerVisibility = this._map.getLayoutProperty(layerName, 'visibility');
        return layerVisibility === 'visible' || layerVisibility === undefined;
    }
    getEnabledLayers() {
        return this._enabledLayers;
    }
    getLayerNames() {
        return Object.keys(this._layersByName);
    }
    getLayerConfig(layerName) {
        return this._layersByName[layerName];
    }
}
exports.default = MapLibreLayerManager;
//# sourceMappingURL=MapLayerManager.js.map